/**
 * Each vendor's account, bill by bill -- Tally's ledger with bill-wise details.
 *
 * The list is every vendor with a bill or an advance: billed, paid, still owed,
 * overdue, and the advance they hold. A vendor opens beside it with two views
 * of the same account. Bills: each one, paid ones too, with what was paid
 * against it and where that leaves it -- and a bill opens its full payment
 * history. Ledger: the entries on their payables and advance, in date order,
 * with a running balance. The two end on the same figure.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import BillPosition from '../components/BillPosition'
import { ErrorNote, Loading, Stat } from '../components/ui'
import { SidePanel, Tabs, matches, rowProps } from '../components/SidePanel'

interface VendorSummary {
  party_id: string
  name: string
  code: string | null
  bills: number
  billed_paise: number
  paid_paise: number
  outstanding_paise: number
  overdue_paise: number
  overdue_bills: number
  advance_paise: number
  last_paid_on: string | null
}

interface Movement {
  on: string
  how: string
  reference: string | null
  amount_paise: number
  reversed: boolean
  journal_number: string | null
  note: string | null
}

interface BillLine {
  invoice_id: string
  reference: string
  supplier_invoice_no: string
  invoice_date: string
  due_date: string
  total_paise: number
  tds_paise: number
  payable_paise: number
  paid_paise: number
  outstanding_paise: number
  status: string
  days_overdue: number
  movements: Movement[]
}

interface LedgerLine {
  on: string
  journal_number: string | null
  narration: string
  account: string
  debit_paise: number
  credit_paise: number
  balance_paise: number
}

interface VendorLedgerDetail {
  party_id: string
  name: string
  since: string
  upto: string
  advance_paise: number
  bills: BillLine[]
  opening_paise: number
  lines: LedgerLine[]
  closing_paise: number
}

const STATUS: Record<string, string> = {
  unpaid: 'Unpaid',
  partially_paid: 'Partly paid',
  paid: 'Paid',
  overdue: 'Overdue',
}

/** What we owe the vendor (Cr), or what they hold of ours (Dr). */
function owed(paise: number): string {
  if (paise === 0) return formatInr(0)
  return `${formatInr(Math.abs(paise))} ${paise > 0 ? 'Cr' : 'Dr'}`
}

export default function VendorLedger() {
  const [open, setOpen] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [onlyOwing, setOnlyOwing] = useState(true)
  const vendors = useQuery({
    queryKey: ['vendor-ledger'],
    queryFn: () => api.get<VendorSummary[]>('/vendor-ledger'),
  })

  if (vendors.isLoading) return <Loading what="Loading vendors" />
  const rows = vendors.data ?? []
  const shown = rows.filter(
    (v) =>
      (!onlyOwing || v.outstanding_paise > 0 || v.advance_paise > 0) &&
      matches(search, v.name, v.code),
  )
  const opened = rows.find((v) => v.party_id === open)
  const sum = (pick: (v: VendorSummary) => number) => rows.reduce((t, v) => t + pick(v), 0)

  return (
    <>
      <ErrorNote error={vendors.error} />
      <div className="grid">
        <Stat
          label="Owed to vendors"
          value={formatInr(sum((v) => v.outstanding_paise))}
          note={`${rows.filter((v) => v.outstanding_paise > 0).length} vendors`}
        />
        <Stat
          label="Overdue"
          value={formatInr(sum((v) => v.overdue_paise))}
          note={`${sum((v) => v.overdue_bills)} bills past their due date`}
        />
        <Stat
          label="Advances held"
          value={formatInr(sum((v) => v.advance_paise))}
          note="paid ahead of a bill, to set against it"
        />
        <Stat
          label="Paid against bills"
          value={formatInr(sum((v) => v.paid_paise))}
          note="this year’s bills, so far"
        />
      </div>

      <section className="card wide">
        <header className="appr-toolbar">
          <h2>Vendors</h2>
          <div className="appr-filters">
            <input
              type="search"
              placeholder="Vendor"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search vendors"
            />
            <label className="appr-check">
              <input
                type="checkbox"
                checked={onlyOwing}
                onChange={(e) => setOnlyOwing(e.target.checked)}
              />
              only those we owe or who hold an advance
            </label>
            <Link to="/books/report-library/vendor-outstanding">Outstanding report</Link>
          </div>
        </header>
        <div className="body tight">
          {shown.length === 0 ? (
            <div className="empty">
              {rows.length === 0
                ? 'No vendor has a posted bill yet.'
                : onlyOwing && !search
                  ? 'Nothing is owed to any vendor and nobody holds an advance. Untick the box to see vendors who are paid up.'
                  : 'Nobody matches that.'}
            </div>
          ) : (
            <table className="appr-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th className="num">Bills</th>
                  <th className="num">Billed</th>
                  <th className="num">Paid</th>
                  <th className="num">Outstanding</th>
                  <th className="num">Overdue</th>
                  <th className="num">Advance</th>
                  <th>Last paid</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((v) => (
                  <tr
                    key={v.party_id}
                    {...rowProps(() => setOpen(v.party_id), open === v.party_id)}
                  >
                    <td>
                      <div className="strong">{v.name}</div>
                      {v.code && <div className="cell-sub mono">{v.code}</div>}
                    </td>
                    <td className="num">{v.bills}</td>
                    <td className="num">{formatInr(v.billed_paise)}</td>
                    <td className="num">{formatInr(v.paid_paise)}</td>
                    <td className="num">
                      <strong>{formatInr(v.outstanding_paise)}</strong>
                    </td>
                    <td className="num">
                      {v.overdue_paise > 0 ? (
                        <span className="pill overdue">{formatInr(v.overdue_paise)}</span>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td className="num">
                      {v.advance_paise > 0 ? (
                        formatInr(v.advance_paise)
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td className="muted date">{v.last_paid_on ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {opened && (
        <VendorPanel key={opened.party_id} vendor={opened} onClose={() => setOpen(null)} />
      )}
    </>
  )
}

function VendorPanel({ vendor, onClose }: { vendor: VendorSummary; onClose: () => void }) {
  const { can } = useAuth()
  const [tab, setTab] = useState<'bills' | 'ledger'>('bills')
  const [since, setSince] = useState('')
  const [upto, setUpto] = useState('')
  const [bill, setBill] = useState<string | null>(null)
  const query = new URLSearchParams()
  if (since) query.set('since', since)
  if (upto) query.set('upto', upto)
  const qs = query.toString()
  const detail = useQuery({
    queryKey: ['vendor-ledger', vendor.party_id, qs],
    queryFn: () =>
      api.get<VendorLedgerDetail>(`/vendor-ledger/${vendor.party_id}${qs ? `?${qs}` : ''}`),
  })
  const d = detail.data
  const statement = `party_id=${vendor.party_id}${qs ? `&${qs}` : ''}`

  return (
    <SidePanel
      kicker="Vendor ledger"
      title={vendor.name}
      sub={
        <>
          {vendor.bills} bills · {formatInr(vendor.paid_paise)} paid ·{' '}
          {vendor.advance_paise > 0 && `${formatInr(vendor.advance_paise)} advance held · `}
          <Link to={`/books/report-library/vendor-statement?${statement}`}>Statement</Link>
          {can('report.any.export') && (
            <>
              {' '}
              · <a href={`/api/v1/reports/pack/vendor-statement/pdf?${statement}`}>PDF</a> ·{' '}
              <a href={`/api/v1/reports/pack/vendor-statement/xlsx?${statement}`}>Excel</a>
            </>
          )}
        </>
      }
      amount={formatInr(vendor.outstanding_paise)}
      wide
      onClose={onClose}
    >
      <ErrorNote error={detail.error} />
      <div className="row">
        <Tabs
          tabs={[
            { key: 'bills', label: 'Bills', count: d?.bills.length },
            { key: 'ledger', label: 'Ledger', count: d?.lines.length },
          ]}
          value={tab}
          onChange={setTab}
        />
        <label className="shrink">
          <span className="caption">From</span>
          <input
            type="date"
            value={since || d?.since || ''}
            onChange={(e) => setSince(e.target.value)}
          />
        </label>
        <label className="shrink">
          <span className="caption">To</span>
          <input
            type="date"
            value={upto || d?.upto || ''}
            onChange={(e) => setUpto(e.target.value)}
          />
        </label>
      </div>

      {!d ? (
        <Loading what="Loading the account" />
      ) : tab === 'bills' ? (
        <>
          {d.bills.length === 0 ? (
            <div className="empty">No posted bills.</div>
          ) : (
            <table className="appr-table">
              <thead>
                <tr>
                  <th>Bill</th>
                  <th>Date / due</th>
                  <th className="num">Bill amount</th>
                  <th className="num">Paid</th>
                  <th className="num">Outstanding</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {d.bills.map((b) => (
                  <BillRows
                    key={b.invoice_id}
                    bill={b}
                    onOpen={() => setBill(bill === b.invoice_id ? null : b.invoice_id)}
                  />
                ))}
              </tbody>
            </table>
          )}
          {bill && <BillPosition invoiceId={bill} onClose={() => setBill(null)} />}
        </>
      ) : (
        <table className="appr-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Particulars</th>
              <th>Entry</th>
              <th className="num">Debit</th>
              <th className="num">Credit</th>
              <th className="num">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="muted date">{d.since}</td>
              <td className="faint">Opening balance</td>
              <td />
              <td />
              <td />
              <td className="num">{owed(d.opening_paise)}</td>
            </tr>
            {d.lines.map((line, i) => (
              <tr key={i}>
                <td className="muted date">{line.on}</td>
                <td>
                  {line.narration}
                  <div className="cell-sub">{line.account}</div>
                </td>
                <td className="mono">{line.journal_number}</td>
                <td className="num">{line.debit_paise ? formatInr(line.debit_paise) : ''}</td>
                <td className="num">{line.credit_paise ? formatInr(line.credit_paise) : ''}</td>
                <td className="num">{owed(line.balance_paise)}</td>
              </tr>
            ))}
            <tr>
              <td className="muted date">{d.upto}</td>
              <td>
                <strong>Closing balance</strong>
              </td>
              <td />
              <td />
              <td />
              <td className="num">
                <strong>{owed(d.closing_paise)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      )}
      <p className="faint">
        Cr is what we owe the vendor; Dr is money they hold of ours — an advance not yet set against
        a bill. The bills and the ledger end on the same figure.
      </p>
    </SidePanel>
  )
}

function BillRows({ bill, onOpen }: { bill: BillLine; onOpen: () => void }) {
  return (
    <>
      <tr {...rowProps(onOpen, false)} title="Open its full payment history">
        <td>
          <span className="mono">{bill.supplier_invoice_no}</span>
          <div className="cell-sub">
            <span className="ref">{bill.reference}</span>
          </div>
        </td>
        <td className="muted date">
          {bill.invoice_date}
          <div className="cell-sub">
            due {bill.due_date}
            {bill.days_overdue > 0 && ` · ${bill.days_overdue} days late`}
          </div>
        </td>
        <td className="num">
          {formatInr(bill.payable_paise)}
          {bill.tds_paise > 0 && (
            <div className="cell-sub">after {formatInr(bill.tds_paise)} TDS</div>
          )}
        </td>
        <td className="num">{formatInr(bill.paid_paise)}</td>
        <td className="num">
          <strong>{formatInr(bill.outstanding_paise)}</strong>
        </td>
        <td>
          <span className={`pill ${bill.status}`}>{STATUS[bill.status] ?? bill.status}</span>
        </td>
      </tr>
      {bill.movements.map((m, i) => (
        <tr key={i} style={{ textDecoration: m.reversed ? 'line-through' : undefined }}>
          <td colSpan={2} className="muted" style={{ paddingLeft: 28 }}>
            {m.on} · {m.how}
            {m.reversed && ' (reversed)'}
          </td>
          <td className="muted">
            {m.reference && <span className="ref">{m.reference}</span>}{' '}
            {m.journal_number && <span className="mono faint">{m.journal_number}</span>}
          </td>
          <td className="num muted">{formatInr(m.amount_paise)}</td>
          <td colSpan={2} />
        </tr>
      ))}
    </>
  )
}
